import React, { useEffect, useState } from "react";
import {
  Loader2, UserRound, Link2, Copy, Trash2, RefreshCw, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

import {
  getStaffAvailability, issueStaffMobileToken, listStaffMobileTokens,
  revokeStaffMobileToken,
} from "@/lib/api";

const AVAIL_TINTS = {
  available: { color: "#0F6D5D", bg: "rgba(15,109,93,0.12)", label: "Available" },
  engaged: { color: "#7A5E12", bg: "rgba(212,175,55,0.18)", label: "Engaged" },
  busy: { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)", label: "Busy" },
};

const EXPIRY_TINTS = {
  ok: { color: "#0F6D5D", bg: "rgba(15,109,93,0.12)", icon: ShieldCheck, label: "Credentials current" },
  expiring_soon: { color: "#7A5E12", bg: "rgba(212,175,55,0.18)", icon: ShieldAlert, label: "Expiring soon" },
  expired: { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)", icon: ShieldAlert, label: "Expired" },
};

export default function StaffAssignmentPanel({
  staff = [], loading, onRefresh, onSelectJob, onSessionExpired,
}) {
  const [tokenDialogStaff, setTokenDialogStaff] = useState(null);
  const [tokens, setTokens] = useState({});  // staff_id -> token record

  const refreshTokens = async () => {
    try {
      const res = await listStaffMobileTokens();
      const map = {};
      (res?.items || []).forEach((t) => { map[t.id] = t; });
      setTokens(map);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); }
    }
  };
  useEffect(() => { refreshTokens(); /* eslint-disable-next-line */ }, []);

  const issueToken = async (name) => {
    try {
      const res = await issueStaffMobileToken(name);
      setTokens((m) => ({ ...m, [res.id]: { id: res.id, staff_name: res.staff_name, token: res.token } }));
      setTokenDialogStaff({ name, ...res });
      toast.success("Mobile link issued.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to issue token.");
    }
  };

  const revoke = async (sid) => {
    if (!window.confirm("Revoke this staff member's mobile link?")) return;
    try {
      await revokeStaffMobileToken(sid);
      setTokens((m) => { const out = { ...m }; delete out[sid]; return out; });
      toast.success("Revoked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed.");
    }
  };

  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}
         data-testid="staff-assignment-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#7A5E12" }}>
            Field Team
          </div>
          <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>
            Staff Assignment
          </h3>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}
                data-testid="staff-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {loading && staff.length === 0 ? (
        <div className="grid place-items-center py-10">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : staff.length === 0 ? (
        <div className="text-center py-8 text-xs" style={{ color: "#64748B" }}>
          <UserRound className="h-7 w-7 mx-auto mb-2" style={{ color: "#94A3B8" }} />
          No employees ready for dispatch yet. Complete their onboarding checklist under <strong>Employees</strong> — only employees in <em>Ready for Dispatch</em> status appear here.
        </div>
      ) : (
        <ul className="space-y-2 max-h-[560px] overflow-y-auto pr-1" data-testid="staff-list">
          {staff.map((s) => {
            const av = AVAIL_TINTS[s.availability] || AVAIL_TINTS.available;
            const ex = EXPIRY_TINTS[s.expiry_status] || EXPIRY_TINTS.ok;
            const ExIcon = ex.icon;
            const tok = tokens[s.id];
            return (
              <li key={s.id} className="rounded-xl border p-2.5"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid={`staff-item-${s.id}`}>
                <div className="flex items-start gap-2">
                  <span className="grid place-items-center h-9 w-9 rounded-full shrink-0 font-display text-sm font-bold"
                        style={{ background: av.bg, color: av.color }}>
                    {(s.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>{s.name}</div>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
                            style={{ background: av.bg, color: av.color }}>
                        {av.label} · {s.workload}
                      </span>
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
                            style={{ background: ex.bg, color: ex.color }}
                            title={s.earliest_expiry ? `Earliest expiry: ${s.earliest_expiry}` : ex.label}>
                        <ExIcon className="h-2.5 w-2.5" /> {s.expiry_status === "ok" ? "Current" : s.expiry_status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Active jobs collapsed */}
                {(s.active_jobs || []).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] cursor-pointer" style={{ color: "#64748B" }}>
                      View {s.active_jobs.length} active job{s.active_jobs.length === 1 ? "" : "s"}
                    </summary>
                    <ul className="mt-1.5 space-y-1 ml-2">
                      {s.active_jobs.map((j) => (
                        <li key={j.id}>
                          <button
                            onClick={() => onSelectJob?.(j.id)}
                            className="text-[11px] text-left w-full hover:underline"
                            style={{ color: "#0B2545" }}
                            data-testid={`staff-job-${j.id}`}
                          >
                            • {j.full_name} <span style={{ color: "#94A3B8" }}>· {j.dispatch_status?.replace(/_/g, " ")}</span>
                            {j.appointment_at && <span style={{ color: "#94A3B8" }}> · {j.appointment_at.slice(0, 16).replace("T", " ")}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {/* Mobile link actions */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {tok ? (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              onClick={() => setTokenDialogStaff({ name: s.name, ...tok })}
                              data-testid={`staff-token-show-${s.id}`}>
                        <Link2 className="h-3 w-3 mr-1" /> Show link
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                              onClick={() => revoke(s.id)}
                              data-testid={`staff-token-revoke-${s.id}`}
                              style={{ color: "#9B1C1C" }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={() => issueToken(s.name)}
                            data-testid={`staff-token-issue-${s.id}`}>
                      <Link2 className="h-3 w-3 mr-1" /> Issue mobile link
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <MobileLinkDialog
        info={tokenDialogStaff}
        onClose={() => setTokenDialogStaff(null)}
      />
    </div>
  );
}

const MobileLinkDialog = ({ info, onClose }) => {
  const url = info?.token
    ? `${window.location.origin}/staff/${encodeURIComponent(info.token)}`
    : "";
  return (
    <Dialog open={!!info} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="staff-link-dialog">
        <DialogHeader>
          <DialogTitle>Mobile field link · {info?.name}</DialogTitle>
          <DialogDescription>
            Send this link to your staff member via text or email. They can update their job
            status (Assigned → En Route → On Site → Completed) from any phone — no login required.
            Keep this link private; anyone with the URL can update their jobs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <div className="rounded-lg border p-2.5 text-[11px] break-all" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
               data-testid="staff-link-url">
            {url}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(url).then(
                () => toast.success("Link copied."),
                () => toast.error("Copy failed."),
              );
            }}
            style={{ background: "#0B2545", color: "#FFFFFF" }}
            data-testid="staff-link-copy"
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
